import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Autocomplete,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Chip,
  Divider,
  Tooltip,
  Snackbar,
} from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { apiUrl } from "config/envConfig";
import { exportToExcel, exportToCSV } from "utils/exportUtils";
// ...existing code...
import AddIcon from "@mui/icons-material/Add";
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from "constants/paginationConstants";
import { TransactionTypeEnum, TransactionTypeOptions } from "constants/transactionTypeConstants";
// useRTL removed (unused)
import WalletTransactionForm from "Components/WalletTransaction/WalletTransactionForm";
import {
  getAllWalletTransactions,
  getWalletTransactionById,
  updateWalletTransactionStatus,
  createWalletTransaction,
} from "services/walletTransaction";
import { getDrivers } from "services/driverService";
// removed unused service imports


// TransactionTypeEnum imported from shared constants
const transactionTypeEnum = TransactionTypeEnum;

const transactionStatusEnum = {
  1: "Pending",
  2: "Success",
  3: "Failed",
  4: "Reversed",
};

// sentinel option removed (unused)

const WalletTransactionPage = () => {
  // Export menu state and handlers (must be inside component)
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const handleExportMenuOpen = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportMenuClose = () => setExportAnchorEl(null);
  // exportColumns removed (unused)

  // Map transactionType and status to readable labels
  const getDirectionValue = (trx) => {
    const raw = trx?.direction ?? trx?.Direction ?? trx?.DirectionId ?? null;
    const num = raw == null ? null : Number(raw);
    return Number.isNaN(num) ? null : num;
  };

  const formatSignedAmount = (amount, direction) => {
    if (amount == null || amount === "") return "";
    const dir = Number(direction);
    if (dir === 1) return `+${Math.abs(Number(amount)).toFixed(2)}`; // Credit
    if (dir === 2) return `-${Math.abs(Number(amount)).toFixed(2)}`; // Debit
    return `${Number(amount).toFixed(2)}`; // unknown/neutral
  };

  const formatFee = (value) => {
    if (value == null || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return `${num.toFixed(2)} JOD`;
  };

  const getExportData = () =>
    transactions.map((trx) => ({
      'Transaction Number': trx.transactionNumber,
      'Driver Name': trx.driverName || '',
      'Type': transactionTypeEnum[trx.transactionType] || '',
      'Amount': formatSignedAmount(trx.amount, getDirectionValue(trx)),
      'Status': transactionStatusEnum[trx.status] || '',
      'Timestamp': trx.timestamp ? new Date(trx.timestamp).toLocaleString() : '',
    }));

  const handleExportCSV = () => {
    exportToCSV(getExportData(), 'wallet_transactions.csv');
    handleExportMenuClose();
  };
  const handleExportExcel = () => {
    exportToExcel(getExportData(), 'wallet_transactions.xlsx');
    handleExportMenuClose();
  };
const statusColors = {
  1: "#FFC107", // Pending - orange
  2: "#4CAF50", // Success - green
  3: "#F44336", // Failed - red
  4: "#9E9E9E", // Reversed - neutral gray
};

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    driverId: "",
    transactionType: "",
    status: "",
    fromDate: "",
    toDate: "",
    orderNumber: "",
  });

  // Driver search input state
  const [driverInputValue, setDriverInputValue] = useState("");

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [copiedTxn, setCopiedTxn] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState({ open: false, message: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [formLoading, setFormLoading] = useState(false);
  // upload progress state removed (not displayed in UI yet)
  const [createInitialData, setCreateInitialData] = useState({ driverIds: [] });
  const location = useLocation();
  const navigate = useNavigate();

  // Status update dialog
  const [statusDialog, setStatusDialog] = useState(false);
  const [selectedTrxId, setSelectedTrxId] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);
  const fetchDrivers = async () => {
    setDriversLoading(true);
    try {
      // Request a large pageSize to load many drivers on mount.
      // Fetch both Regular (1) and Expedited (2) drivers and merge to ensure
      // expedited drivers are included even if the backend defaults to a
      // single type when DriverType is omitted.
      const [res1, res2] = await Promise.all([
        getDrivers(1000, 1, "", "", "", [], null, null, null, null, null, 1),
        getDrivers(1000, 1, "", "", "", [], null, null, null, null, null, 2),
      ]);
      const list1 = res1?.page || [];
      const list2 = res2?.page || [];

      // merge unique by id
      const combined = [...list1];
      const ids = new Set(list1.map((d) => String(d.id)));
      for (const d of list2) {
        if (!ids.has(String(d.id))) {
          combined.push(d);
          ids.add(String(d.id));
        }
      }
      setDrivers(combined);
    } catch (err) {
      console.error("Failed to fetch drivers", err);
      setDrivers([]);
    } finally {
      setDriversLoading(false);
    }
  };

  // Debounced remote search when user types in the driver Autocomplete
  const driverSearchRef = useRef(null);
  const searchDrivers = (term) => {
    if (driverSearchRef.current) clearTimeout(driverSearchRef.current);
    driverSearchRef.current = setTimeout(async () => {
      setDriversLoading(true);
      try {
        // Search both driver types (Regular + Expedited) to ensure results
        // include expedited drivers regardless of backend defaults.
        const [r1, r2] = await Promise.all([
          getDrivers(100, 1, term, "", "", [], null, null, null, null, null, 1),
          getDrivers(100, 1, term, "", "", [], null, null, null, null, null, 2),
        ]);
        const a = r1?.page || [];
        const b = r2?.page || [];
        const combined = [...a];
        const ids = new Set(a.map((d) => String(d.id)));
        for (const d of b) {
          if (!ids.has(String(d.id))) {
            combined.push(d);
            ids.add(String(d.id));
          }
        }
        // Preserve any currently selected driver so it doesn't disappear
        // if the server search result doesn't include it.
        if (selectedDriver && !combined.some((d) => String(d.id) === String(selectedDriver.id))) {
          setDrivers([selectedDriver, ...combined]);
        } else {
          setDrivers(combined || []);
        }
      } catch (e) {
        console.error('Driver search failed', e);
      } finally {
        setDriversLoading(false);
      }
    }, 300);
  };
  const fetchTransactions = useCallback(async (pageNum = DEFAULT_PAGE, pageSize = DEFAULT_ROWS_PER_PAGE) => {
    setLoading(true);
    try {
      const filterPayload = {
        pageNumber: pageNum,
        pageSize: pageSize,
        driverId: filters.driverId ? filters.driverId : null,
        orderNumber: filters.orderNumber ? filters.orderNumber : null,
        type: filters.transactionType ? parseInt(filters.transactionType) : null,
        status: filters.status ? parseInt(filters.status) : null,
        from: filters.fromDate ? new Date(filters.fromDate).toISOString() : null,
        to: filters.toDate ? new Date(filters.toDate).toISOString() : null,
        sortBy: "timestamp", // Sort by timestamp field
        sortDescending: true, // Sort in descending order (newest first)
      };

      const data = await getAllWalletTransactions(filterPayload);

      // Normalize possible response shapes so `transactions` is always an array
      let txList = [];
      let total = 0;

      if (Array.isArray(data)) {
        txList = data;
        total = data.length;
      } else if (data) {
        if (Array.isArray(data.items)) {
          txList = data.items;
          total = data.totalCount || data.totalRecords || txList.length;
        } else if (Array.isArray(data.list)) {
          txList = data.list;
          total = data.totalCount || data.totalRecords || txList.length;
        } else if (Array.isArray(data.data)) {
          txList = data.data;
          total = data.totalCount || data.totalRecords || txList.length;
        } else {
          // Fallback: if data has keys but isn't one of the above shapes, try to find array value
          const arr = Object.values(data).find((v) => Array.isArray(v));
          if (Array.isArray(arr)) {
            txList = arr;
            total = data.totalCount || data.totalRecords || txList.length;
          }
        }
      }

      // Ensure transactions are ordered by timestamp (newest first) on the client
      const getTimestamp = (t) => {
        if (!t) return 0;
        return (
          Date.parse(t.timestamp || t.createdAt || t.creationDate || t.createdDate || t.date || t.time) || 0
        );
      };

      // Safe date formatter is declared in component scope (moved)

      const sorted = (txList || []).slice().sort((a, b) => getTimestamp(b) - getTimestamp(a));

      setTransactions(sorted);
      setTotalCount(total);
      setError(null);
    } catch (err) {
      console.error("Failed to load wallet transactions", err);
      setError("Failed to load transactions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTransactions(page, rowsPerPage);
    fetchDrivers(); // Fetch drivers on component mount
  }, [page, rowsPerPage, fetchTransactions]);

  // Safe date formatter: returns formatted string or '—' when missing/invalid
  const formatDateSafe = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    if (d.getFullYear && d.getFullYear() < 1900) return "—";
    return d.toLocaleString();
  };

  // Build absolute URL for files (prefix with apiUrl if relative)
  const getFullUrl = (f) => {
    const raw = f?.url || f?.path || f?.fileUrl || f?.Url || f?.pathUrl || "";
    if (!raw) return null;
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    const base = (apiUrl || "").replace(/\/$/, "");
    if (!base) return raw;
    if (raw.startsWith("/")) return `${base}${raw}`;
    return `${base}/${raw}`;
  };

  const openFile = (f) => {
    const full = getFullUrl(f);
    if (!full) {
      setSnackbar({ open: true, message: "❌ No file URL available", severity: "error" });
      return;
    }
    try {
      window.open(full, "_blank", "noopener,noreferrer");
    } catch (e) {
      window.location.href = full;
    }
  };

  const downloadFile = async (f) => {
    const full = getFullUrl(f);
    if (!full) {
      setSnackbar({ open: true, message: "❌ No file URL available", severity: "error" });
      return;
    }

    try {
      const resp = await fetch(full, { method: 'GET', credentials: 'include' });
      if (!resp.ok) throw new Error(`Failed to download (${resp.status})`);
      const blob = await resp.blob();
      const cd = resp.headers.get('content-disposition');
      let filename = f.fileName || f.name || f.fileName || `file`;
      if (cd) {
        const m1 = /filename\*=UTF-8''(.+)$/.exec(cd);
        const m2 = /filename="(.+)"/.exec(cd);
        const m3 = /filename=(.+);?/.exec(cd);
        const m = m1 || m2 || m3;
        if (m && m[1]) {
          try { filename = decodeURIComponent(m[1]); } catch(e) { filename = m[1]; }
        }
      }
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Download failed', e);
      setSnackbar({ open: true, message: '❌ Failed to download file', severity: 'error' });
    }
  };

  // keep selectedDriver in sync with filters.driverId and drivers list
  useEffect(() => {
    if (!filters.driverId) {
      setSelectedDriver(null);
      return;
    }
    const found = drivers.find((d) => String(d.id) === String(filters.driverId));
    setSelectedDriver(found || null);
  }, [drivers, filters.driverId]);

  // If navigated here with state to open create dialog, handle it
  useEffect(() => {
    if (location && location.state && location.state.openCreate) {
      const payload = {};
      if (location.state.transactionType) payload.transactionType = String(location.state.transactionType);
      if (location.state.driverId) payload.driverIds = [location.state.driverId];
      openCreateWithPayload(payload);
      // clear navigation state so it doesn't reopen on refresh/navigation
      try {
        navigate(location.pathname, { replace: true, state: {} });
      } catch (e) {
        // ignore
      }
    }
  }, [location, navigate]);

  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({ ...prev, [filterKey]: value }));
    setPage(DEFAULT_PAGE);
  };

  const handleApplyFilters = () => {
    setPage(DEFAULT_PAGE);
    fetchTransactions(DEFAULT_PAGE, rowsPerPage);
  };

  const handleResetFilters = () => {
    setFilters({
      driverId: "",
      transactionType: "",
      status: "",
      fromDate: "",
      toDate: "",
      orderNumber: "",
    });
    setDriverInputValue("");
    setPage(DEFAULT_PAGE);
  };

  const getOrderIdFromTransaction = (trx) => {
    if (!trx) return null;
    if (trx.orderId) return trx.orderId;
    if (Array.isArray(trx.orderIds) && trx.orderIds.length > 0) return trx.orderIds[0];
    return null;
  };

  const handleOpenCreateDialog = () => {
    setDialogMode("create");
    setSelectedTransaction(null);
    setCreateInitialData({ driverIds: [] });
    setOpenDialog(true);
  };

  const openCreateWithPayload = (payload = {}) => {
    setDialogMode("create");
    setSelectedTransaction(null);
    setCreateInitialData({ driverIds: [], ...payload });
    setOpenDialog(true);
  };

  // handleOpenEditDialog removed (edit action not implemented)

  const handleOpenViewDialog = async (transaction) => {
    try {
      setFormLoading(true);
      // fetch fresh details from server to ensure files/metadata present
      const data = await getWalletTransactionById(transaction.id || transaction).catch(() => null);
      // Merge the row data (transaction) with the server response so we keep
      // displayable fields present in the table if the detail endpoint
      // doesn't include them.
      const merged = { ...(transaction || {}), ...(data || {}) };
      setSelectedTransaction(merged);
      setViewDialogOpen(true);
    } catch (err) {
      console.error("Failed to fetch transaction details", err);
      setError("Failed to load transaction details");
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedTransaction(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTransaction(null);
  };

const handleFormSubmit = async (formData) => {
  const {
    driverIds,
    amount,
    transactionType,
  } = formData;

  // ✅ Correct validation using submitted data
  if (!driverIds?.length || !amount || !transactionType) {
    alert("Please fill in all required fields");
    return;
  }

  setFormLoading(true);
  try {
    // No server-side cash-out rule should be enforced here; wallet-transactions
    // are allowed to create transactions directly. We still perform minimal
    // checks in the form; backend will reject if wallet isn't found.

    // upload progress handler
    const onUploadProgress = (progressEvent) => {
      // upload progress available; currently not displayed in UI.
    };

    await createWalletTransaction(formData, onUploadProgress);
    await fetchTransactions(DEFAULT_PAGE, rowsPerPage);
    handleCloseDialog();
    setSnackbar({ open: true, message: "✅ Transaction saved successfully", severity: "success" });
  } catch (err) {
    console.error("Failed to save transaction", err);
    const raw = err?.response?.data;
    let display = err?.response?.data?.message || err?.message || "Failed to save. Please try again.";
    if (!display && raw) {
      if (typeof raw === 'string') display = raw;
      else {
        const vals = Object.values(raw).flatMap((v) => (Array.isArray(v) ? v : [v]));
        const found = vals.find((v) => typeof v === 'string' && v.trim().length > 3);
        if (found) display = found;
        else display = JSON.stringify(raw).slice(0, 400);
      }
    }
    setSnackbar({ open: true, message: display, severity: "error" });
    // rethrow so parent form component knows submission failed and does not
    // display a success notification
    throw err;
  } finally {
    setFormLoading(false);
  }
};


  const handleOpenStatusDialog = (transaction) => {
    setSelectedTrxId(transaction.id);
    setNewStatus(transaction.status);
    setStatusDialog(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialog(false);
    setSelectedTrxId(null);
    setNewStatus("");
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;

    setStatusLoading(true);
    try {
      await updateWalletTransactionStatus(selectedTrxId, parseInt(newStatus));
      await fetchTransactions(page, rowsPerPage);
      handleCloseStatusDialog();
      setSnackbar({ open: true, message: "✅ Transaction status updated successfully", severity: "success" });
    } catch (err) {
      console.error("Failed to update status", err);
      const msg = err.response?.data?.message || "Failed to update status. Please try again.";
      setError(msg);
      setSnackbar({ open: true, message: `❌ ${msg}`, severity: "error" });
    } finally {
      setStatusLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(DEFAULT_PAGE);
  };

  return (
    <Box sx={{ padding: "2rem", position: "relative" }}>
      <Typography variant="h1" sx={{ marginBottom: "2rem" }}>
        Wallet Transactions
      </Typography>

      {error && <Alert severity="error" sx={{ marginBottom: "1rem" }}>{error}</Alert>}

      {/* Filters Section */}
      <Paper sx={{ padding: "1.5rem", marginBottom: "2rem" }}>
        <Typography variant="h6" sx={{ marginBottom: "1rem" }}>
          Filters
        </Typography>
        <Stack spacing={2}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 2, alignItems: 'center' }}>
            <Autocomplete
              size="small"
              fullWidth
              options={drivers}
              getOptionLabel={(option) => {
                if (!option) return "";
                return `${option.fullName || "Unknown"} ${option.phone ? `(${option.phone})` : ""}`;
              }}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              inputValue={driverInputValue}
              value={selectedDriver}
              onChange={(event, newValue) => {
                try { console.debug('Driver Autocomplete onChange, newValue=', newValue); } catch (e) {}
                setSelectedDriver(newValue || null);
                if (newValue) {
                  handleFilterChange("driverId", String(newValue.id));
                  // keep input showing selected driver's label
                  setDriverInputValue(`${newValue.fullName || "Unknown"}${newValue.phone ? ` (${newValue.phone})` : ""}`);
                  setDrivers((prev) => {
                    if (!prev || !Array.isArray(prev)) return [newValue];
                    if (prev.some((d) => String(d.id) === String(newValue.id))) return prev;
                    return [newValue, ...prev];
                  });
                } else {
                  handleFilterChange("driverId", "");
                  setDriverInputValue("");
                }
              }}
              onInputChange={(event, newInputValue) => {
                setDriverInputValue(newInputValue);
                try { if (newInputValue && newInputValue.trim().length > 0) searchDrivers(newInputValue); } catch (e) {}
              }}
              autoSelect={true}
              openOnFocus={true}
              loading={driversLoading}
              ListboxProps={{ style: { maxHeight: 300 } }}
              renderInput={(params) => (
                <TextField {...params} label="Driver" placeholder="Search driver by name or phone" margin="none" />
              )}
              noOptionsText="No drivers found"
            />

            <FormControl size="small" fullWidth variant="outlined" margin="none">
              <InputLabel>Transaction Type</InputLabel>
              <Select
                value={filters.transactionType}
                label="Transaction Type"
                onChange={(e) => handleFilterChange("transactionType", e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                {TransactionTypeOptions.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth variant="outlined" margin="none">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                {Object.entries(transactionStatusEnum).map(([key, label]) => (
                  <MenuItem key={key} value={key} style={{ color: statusColors[key] }}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Order Number"
              value={filters.orderNumber}
              onChange={(e) => handleFilterChange("orderNumber", e.target.value)}
              size="small"
              fullWidth
              margin="none"
              placeholder="e.g. 12345"
            />

            <TextField
              label="From Date"
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              size="small"
              fullWidth
              margin="none"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="To Date"
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
              size="small"
              fullWidth
              margin="none"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{ backgroundColor: "#2A3467", height: "40px" }}
            >
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              onClick={handleResetFilters}
              sx={{ borderColor: "#2A3467", color: "#2A3467", height: "40px" }}
            >
              Reset
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Create & Export Buttons (right-aligned) */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, marginBottom: "1rem" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{ backgroundColor: "#2A3467" }}
        >
          Create Transaction
        </Button>
        <IconButton
          aria-label="Export"
          onClick={handleExportMenuOpen}
          sx={{ border: '1px solid #2A3467', color: '#2A3467', ml: 1 }}
        >
          <FileDownloadIcon />
        </IconButton>
        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={handleExportMenuClose}
        >
          <MenuItem onClick={handleExportCSV}>
            <ListItemIcon><TableChartIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Export as CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportExcel}>
            <ListItemIcon><TableChartIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Export as Excel</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Transactions Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ marginTop: "1rem", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Transaction Number</TableCell>
                <TableCell>Order Number</TableCell>
                <TableCell>Driver Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Driver Fees</TableCell>
                <TableCell align="right">Fanoos Fees</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ textAlign: "center", padding: "2rem" }}>
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((trx) => (
                  <TableRow key={trx.id}>
                    <TableCell>{trx.transactionNumber}</TableCell>
                    <TableCell>
                      {(() => {
                        const orderNumber =
                          trx.orderNumber ?? trx.OrderNumber ?? trx.order?.orderNumber ?? trx.order?.OrderNumber ?? "—";
                        const orderId = getOrderIdFromTransaction(trx);
                        if (!orderId || orderNumber === "—") return orderNumber;
                        return (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => navigate(`/orders/${orderId}`)}
                            sx={{ padding: 0, minWidth: 0, textTransform: "none" }}
                          >
                            {orderNumber}
                          </Button>
                        );
                      })()}
                    </TableCell>
                    <TableCell>{trx.driverName || "—"}</TableCell>
                    <TableCell>{transactionTypeEnum[trx.transactionType] || "Unknown"}</TableCell>
                    <TableCell align="right">
                      {(() => {
                        const dir = getDirectionValue(trx);
                        const isCredit = dir === 1;
                        const isDebit = dir === 2;
                        const color = isCredit ? 'success.main' : isDebit ? 'error.main' : 'text.primary';
                        const sign = isCredit ? '+' : isDebit ? '-' : '';
                        return (
                          <Typography sx={{ fontWeight: 700, color }}>
                            {`${sign}${(Math.abs(Number(trx.amount) || 0)).toFixed(2)} JOD`}
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      {formatFee(trx.driverFees ?? trx.DriverFees ?? trx.driverFee ?? trx.DriverFee)}
                    </TableCell>
                    <TableCell align="right">
                      {formatFee(trx.fanoosFees ?? trx.FanoosFees ?? trx.fanoosFee ?? trx.FanoosFee)}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "4px",
                          backgroundColor: statusColors[trx.status],
                          color: "#fff",
                          fontSize: "0.875rem",
                          fontWeight: "bold",
                        }}
                      >
                        {transactionStatusEnum[trx.status] || "Unknown"}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(trx.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenStatusDialog(trx)}
                        >
                          Update Status
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleOpenViewDialog(trx)}
                        >
                          View
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Create/Edit Transaction Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === "create" ? "Create New Transaction" : "Edit Transaction"}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "1rem" }}>
          <WalletTransactionForm
            initialData={dialogMode === "create" ? createInitialData : selectedTransaction}
            onSubmit={handleFormSubmit}
            isLoading={formLoading}
            drivers={drivers}
            driversLoading={driversLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusDialog} onClose={handleCloseStatusDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Update Transaction Status</DialogTitle>
        <DialogContent sx={{ paddingTop: "1rem" }}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              {Object.entries(transactionStatusEnum).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancel</Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={statusLoading}
            sx={{ backgroundColor: "#2A3467" }}
          >
            {statusLoading ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* View Transaction Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent dividers>
          {selectedTransaction ? (
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              {/* Left panel: prominent amount and status */}
              <Box sx={{ minWidth: 220, flex: '0 0 240px', p: 2, borderRadius: 2, background: 'linear-gradient(135deg,#f5f7ff,#e6f0ff)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Amount</Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mt: 0.5,
                    color: getDirectionValue(selectedTransaction) === 1 ? 'success.main' : 'error.main',
                  }}
                >
                  {selectedTransaction.amount != null
                    ? `${getDirectionValue(selectedTransaction) === 1 ? '+' : '-'}${Number(Math.abs(selectedTransaction.amount)).toFixed(2)} JOD`
                    : '—'}
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip label={transactionStatusEnum[selectedTransaction.status] || 'Unknown'} sx={{ bgcolor: statusColors[selectedTransaction.status] || '#ccc', color: '#fff', fontWeight: 700 }} />

                  <Chip label={( () => {
                    const pm = selectedTransaction.paymentMethod ?? selectedTransaction.paymentMethodId ?? selectedTransaction.paymentMethodType;
                    const map = {1: 'Cash',2: 'Visa',3: 'MasterCard',4: 'Efawateercom',5: 'Bank Transfer',6: 'System'};
                    return map[pm] || (pm ?? '—');
                  })()} variant="outlined" />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Type</Typography>
                <Typography sx={{ fontWeight: 600 }}>{transactionTypeEnum[selectedTransaction.transactionType] || '—'}</Typography>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Created</Typography>
                  <Typography>{formatDateSafe(selectedTransaction.timestamp || selectedTransaction.createdAt)}</Typography>
                </Box>
              </Box>

              {/* Right panel: details */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: '#2A3467' }}>{(selectedTransaction.driverName || selectedTransaction.driver?.fullName || 'U').charAt(0)}</Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>{selectedTransaction.driverName || selectedTransaction.driver?.fullName || 'Unknown Driver'}</Typography>
                    {selectedTransaction.driverPhone && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{selectedTransaction.driverPhone}</Typography>}
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'center' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Transaction Number</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{selectedTransaction.transactionNumber || '—'}</Typography>
                        <Tooltip title={copiedTxn === selectedTransaction.transactionNumber ? "Copied" : "Copy transaction number"}>
                          <IconButton
                            size="small"
                            onClick={async () => {
                              const txt = selectedTransaction.transactionNumber || "";
                              if (!txt) return;
                              try {
                                await navigator.clipboard.writeText(txt);
                                setCopiedTxn(txt);
                                setCopySnackbar({ open: true, message: "Transaction number copied" });
                                setTimeout(() => {
                                  if (copiedTxn === txt) setCopiedTxn(null);
                                }, 2000);
                              } catch (e) {
                                setCopySnackbar({ open: true, message: "Failed to copy" });
                              }
                            }}
                          >
                            {copiedTxn === selectedTransaction.transactionNumber ? (
                              <CheckIcon fontSize="small" />
                            ) : (
                              <ContentCopyIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                    </Box>
                  </Box>

                  {(selectedTransaction.referenceNumber || selectedTransaction.reference) && (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Payment Reference</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 600 }}>{selectedTransaction.referenceNumber || selectedTransaction.reference}</Typography>
                        <Tooltip title="Copy reference">
                          <IconButton size="small" onClick={() => navigator.clipboard?.writeText(selectedTransaction.referenceNumber || selectedTransaction.reference || '')}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                </Box>

                {selectedTransaction.description && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Description</Typography>
                    <Typography>{selectedTransaction.description}</Typography>
                  </Box>
                )}

                {selectedTransaction.files && Array.isArray(selectedTransaction.files) && selectedTransaction.files.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>Attachments</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedTransaction.files.map((f, i) => {
                        const full = getFullUrl(f);
                        const name = f.fileName || f.name || `File ${i+1}`;
                        const isImage = full && /\.(jpg|jpeg|png|gif|webp)$/i.test(full);
                        return (
                          <Paper key={i} variant="outlined" sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', minWidth: 140 }}>
                            {isImage ? (
                              <Box component="img" src={full} sx={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 1 }} />
                            ) : (
                                  <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {/\.pdf$/i.test(name) ? <PictureAsPdfIcon color="error" /> : <ImageIcon />}
                                  </Box>
                                )}
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '0.875rem' }}>{name}</Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                <Button size="small" onClick={() => openFile(f)} startIcon={<OpenInNewIcon />}>
                                  Open
                                </Button>
                                <Button size="small" onClick={() => downloadFile(f)} startIcon={<FileDownloadIcon />}>
                                  Download
                                </Button>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setCopySnackbar({ ...copySnackbar, open: false })} severity="success" sx={{ width: "100%" }}>
          {copySnackbar.message}
        </Alert>
      </Snackbar>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WalletTransactionPage;
